import { atom, useAtomValue, useSetAtom } from 'jotai';
import Image from 'next/image';
import * as React from 'react';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  AtSymbolIcon,
  ChevronUpDownIcon,
  DevicePhoneMobileIcon,
  DocumentCheckIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import Router, { useRouter } from 'next/router';
import { AssociationBadge } from '@restworld/ui-components';
import { Button, IconButton } from '@restworld/ui-ds-base';
import { WhatsappIcon, customImageLoader, partnerLandingUrl, useAnalytics } from '@restworld/utils-common';

import {
  workerPastExperiencesAtom,
  workerPersonalDetailsAtom,
  workerProfessionalAssociationsAtom,
  workerProfileAtom,
} from '../global-state.worker-data';
import { showUploadCVModalAtom, UploadWorkerCv } from '../profile/upload-worker-cv';
import { useDownloadCv } from '../profile/hooks/use-download-cv';
import { DeleteWorkerCv, showDeleteCVModalAtom } from '../profile/delete-worker-cv-modal';
import {
  showUpdateStatusModalAtom,
  UpdateResearchStatusModal,
  useStatusTranslatedOptions,
} from '../profile/update-research-status-modal';
import ReferencesConsentModal, {
  referencesConsentModalVisibleAtom,
  useCheckIfUserHasDeclinedConsentRequest,
} from './references-consent-modal';
import ReferenceRequestSelectorModal from './references-request-selector-modal';
import ReferencesRequestSuccessMessageModal from './references-request-message-modal';
import ReferencesSendLinkRequestModal from './references-send-link-request-modal';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IProfileDetailsSectionProps {}

export const alreadyConfirmedConsentAtom = atomWithStorage<boolean>(
  'alreadyConfirmedConsent',
  false,
  createJSONStorage<boolean>(() => localStorage)
);

export const isReferencesContentGeneralFlowAtom = atom<boolean>(false);
export const useUserDeclineModalVisibility = () => {
  const setReferencesConsentModalVisible = useSetAtom(referencesConsentModalVisibleAtom);
  const alreadyConfirmedConsent = useAtomValue(alreadyConfirmedConsentAtom);

  const { showDeclineConsentModal } = useCheckIfUserHasDeclinedConsentRequest();
  const setIsReferencesContentGeneralFlowAtom = useSetAtom(isReferencesContentGeneralFlowAtom);
  const router = useRouter();

  React.useEffect(() => {
    if (alreadyConfirmedConsent) return;
    showDeclineConsentModal().then((shouldShow) => {
      if (shouldShow) {
        if (router.query.collect_references === 'true') {
          setReferencesConsentModalVisible(true);
          Router.push(`/profilo/references`, undefined, {
            shallow: true,
          });
        } else
          setTimeout(() => {
            setReferencesConsentModalVisible(true);
          }, 5000);
        setIsReferencesContentGeneralFlowAtom(true);
      }
    });
  }, [
    router.query.collect_references,
    setReferencesConsentModalVisible,
    setIsReferencesContentGeneralFlowAtom,
    alreadyConfirmedConsent,
    showDeclineConsentModal,
  ]);
};

const UserBasicInformationAndCV = () => {
  const workerProfile = useAtomValue(workerProfileAtom);
  const workerPersonalDetails = useAtomValue(workerPersonalDetailsAtom);
  const showUploadCVModal = useSetAtom(showUploadCVModalAtom);
  const showDeleteCVModal = useSetAtom(showDeleteCVModalAtom);
  const { t } = useTranslation('worker-references-page');

  const workerCV = workerProfile?.cv;
  const isCVUploaded = !!workerCV;
  const cvName = `${workerProfile?.public_cv_username || '[filename]'}.pdf`;

  const { handleDownload, isLoading } = useDownloadCv();

  const handleClick = React.useCallback(() => {
    if (!isCVUploaded) {
      showUploadCVModal(true);
    } else {
      handleDownload({ name: cvName, url: workerCV });
    }
  }, [cvName, handleDownload, isCVUploaded, showUploadCVModal, workerCV]);

  return (
    <div className="self-stretch p-3 rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-40 inline-flex flex-col justify-start items-start gap-3">
      <div className="w-full flex flex-col justify-start items-start gap-3">
        {!!workerPersonalDetails?.email && (
          <div className="inline-flex justify-start items-center gap-1 w-full">
            <AtSymbolIcon className="w-4 h-4 relative text-neutral-700" />
            <h5 className="justify-start text-black text-base font-normal leading-tight truncate w-[90%] md:w-full lg:w-[85%]">
              {workerPersonalDetails?.email}
            </h5>
          </div>
        )}
        {!!workerPersonalDetails?.phone_number && (
          <div className="inline-flex justify-start items-center gap-1">
            <DevicePhoneMobileIcon className="w-4 h-4 relative text-neutral-700" />
            <h5 className="justify-start text-black text-base font-normal leading-none">
              {workerPersonalDetails?.phone_number}
            </h5>
          </div>
        )}
        {!!workerPersonalDetails?.whatsapp_phone_number &&
          workerPersonalDetails.whatsapp_phone_number !== workerPersonalDetails.phone_number && (
            <div className="inline-flex justify-start items-center gap-1">
              <WhatsappIcon className="w-4 h-4 text-neutral-700" />
              <h5 className="justify-start text-black text-base font-normal leading-none">
                {workerPersonalDetails?.whatsapp_phone_number}
              </h5>
            </div>
          )}
      </div>
      <hr className="self-stretch h-px relative bg-neutral-40 rounded-full" />
      <div className="self-stretch inline-flex justify-end items-center gap-[5px]">
        <h5 className="flex-1 justify-start text-neutral-700 text-base font-normal">{t('your_cv')}</h5>
        <IconButton
          ariaLabel={isCVUploaded ? 'download-cv' : 'upload-cv'}
          size="sm"
          Icon={isCVUploaded ? ArrowDownTrayIcon : ArrowUpTrayIcon}
          variant="neutral"
          onClick={handleClick}
          loading={isLoading}
        />
        {isCVUploaded && (
          <IconButton
            onClick={() => {
              showDeleteCVModal(true);
            }}
            ariaLabel="delete-cv"
            size="sm"
            Icon={TrashIcon}
            variant="error"
          />
        )}
      </div>
    </div>
  );
};
const UserAvatar = () => {
  const { t } = useTranslation();
  const workerPersonalDetails = useAtomValue(workerPersonalDetailsAtom);
  const professionalAssociations = useAtomValue(workerProfessionalAssociationsAtom);
  return (
    <div className="self-stretch inline-flex justify-start items-center gap-2.5">
      {!!workerPersonalDetails?.picture && (
        <div className="w-14 h-14 rounded-full relative overflow-hidden">
          <Image
            fill
            src={workerPersonalDetails?.picture}
            alt="avatar-image"
            className="object-cover object-top"
            loader={customImageLoader}
          />
        </div>
      )}
      {/* Kept in step with the same block in `UserProfileDetails`: this sidebar
          replaces that one entirely when NEXT_PUBLIC_SHOW_REFERENCES_FEATURE is
          on, so a badge added only there would be invisible wherever the
          references feature is enabled. */}
      <div className="flex flex-col items-start gap-1">
        {!!workerPersonalDetails?.name && (
          <h5 className="justify-start text-neutral-700 text-xl font-semibold leading-none">
            {workerPersonalDetails?.name} {workerPersonalDetails?.surname}
          </h5>
        )}
        {professionalAssociations.length > 0 && (
          <div className="flex flex-row flex-wrap gap-2">
            {professionalAssociations.map((variable) => (
              <AssociationBadge
                key={variable.id}
                label={variable.label}
                description={variable.description}
                iconUrl={variable.icon}
                linkUrl={partnerLandingUrl(variable.key)}
                linkLabel={t('common:find_out_more')}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
const UserResearchStatus = () => {
  const { t } = useTranslation();
  const showUpdateStatusModal = useSetAtom(showUpdateStatusModalAtom);
  const researchStatus = useAtomValue(workerProfileAtom).research_status?.key;
  const workerStatusLabels = useStatusTranslatedOptions();

  const workerStatusLabel = React.useMemo(
    () => workerStatusLabels.find((status) => status.status_key === researchStatus)?.label,
    [researchStatus, workerStatusLabels]
  );
  return (
    <Button onClick={() => showUpdateStatusModal(true)} EndIcon={ChevronUpDownIcon} variant="primary" fullwidth>
      {workerStatusLabel || t('onboarding-page:update_status')}
    </Button>
  );
};
const ManageYourReferences = () => {
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation('worker-references-page');
  const pastExperiences = useAtomValue(workerPastExperiencesAtom);
  return (
    !!pastExperiences?.length && (
      <Link
        href="/profilo/references"
        onClick={() => {
          trackEvent('worker_opens_references_page', {});
        }}
        passHref
        className="w-full"
      >
        <Button
          disabled={!pastExperiences?.length}
          variant="secondary-restworld"
          fullwidth
          EndIcon={DocumentCheckIcon}
          className={'min-w-[300px]'}
        >
          {t('manage_your_references')}
        </Button>
      </Link>
    )
  );
};
export const ProfileDetailsSection: React.FunctionComponent<IProfileDetailsSectionProps> = (props) => {
  useUserDeclineModalVisibility();
  return (
    <>
      <div className="w-full lg:w-96 h-max rounded-2xl lg:sticky lg:top-[7rem] relative outline outline-1 outline-offset-[-1px] outline-neutral-40 bg-neutral-10">
        <div className="px-4 pt-6 pb-4 inline-flex flex-col justify-start items-start gap-6 w-full">
          <div className="self-stretch flex flex-col justify-start items-start gap-3">
            <UserAvatar />
            <UserBasicInformationAndCV />
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-3">
            <UserResearchStatus />
            <ManageYourReferences />
          </div>
        </div>
      </div>
      <UploadWorkerCv showUploadCvButton={false} />
      <DeleteWorkerCv showDeleteCvButton={false} />
      <UpdateResearchStatusModal showUpdateStatusButton={false} />
      <ReferencesConsentModal />
      <ReferenceRequestSelectorModal />
      <ReferencesRequestSuccessMessageModal />
      <ReferencesSendLinkRequestModal />
    </>
  );
};

export default ProfileDetailsSection;
